import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  SelectChangeEvent,
} from "@mui/material";
import {
  AddListingButton,
  EmptyTableRow,
  ListingPageShell,
  bodyCellSx,
  formatListingDate,
  headCellSx,
  BORDER,
  ICON_BLUE,
} from "../AssetListing/assetListingShared";
import { DARK_NAVY, DARK_NAVY_LIGHT, PROFILE_FONT, muiFieldSx } from "../../constants/layout";
import RegisterForm from "./AddPartner";

const fieldSx = muiFieldSx;

interface User {
  id: number;
  firstName: string;
  email: string;
  mobile: string;
  createdAt: string;
  aumUserId: string;
}

interface PartnerTableProps {
  users: User[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCancel: () => void;
  totalPages: number;
  pageSize: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onPageSizeChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  currentPage: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onRefresh: () => void;
}

const PartnerDetailsTable: React.FC<PartnerTableProps> = ({
  users,
  totalPages,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  searchQuery,
  setSearchQuery,
  onRefresh,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const sortedUsers = useMemo(() => {
    return [...(users ?? [])].sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();

      if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
      if (Number.isNaN(aTime)) return 1;
      if (Number.isNaN(bTime)) return -1;
      return bTime - aTime;
    });
  }, [users]);

  const handleAddSuccess = () => {
    setShowAddModal(false);
    onRefresh();
  };

  const totalRecords = totalPages * pageSize;
  const from = sortedUsers.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = sortedUsers.length === 0 ? 0 : (currentPage - 1) * pageSize + sortedUsers.length;

  return (
    <>
      <ListingPageShell
        title="Relation Partners"
        subtitle="View and manage all registered partners."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by name, email or user ID..."
        action={
          <AddListingButton label="Add Partner" onClick={() => setShowAddModal(true)} />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Name</TableCell>
                <TableCell sx={headCellSx}>User ID</TableCell>
                <TableCell sx={headCellSx}>Email</TableCell>
                <TableCell sx={headCellSx}>Mobile</TableCell>
                <TableCell sx={headCellSx}>Registered At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.length ? (
                sortedUsers.map((user) => (
                  <TableRow key={user.id} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                    <TableCell sx={bodyCellSx}>{user.firstName}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY }}>{user.aumUserId}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY }}>{user.email}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY }}>{user.mobile}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY }}>
                      {formatListingDate(user.createdAt)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <EmptyTableRow colSpan={5} message="No partners found" />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 2, md: 2.5 },
            py: 2,
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT }}>
            Showing {from} to {to} of {totalRecords} partners
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ ...fieldSx, minWidth: 130 }}>
              <Select
                value={pageSize}
                onChange={(e: SelectChangeEvent<number>) =>
                  onPageSizeChange({
                    target: { value: String(e.target.value) },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              >
                <MenuItem value={10}>10 per page</MenuItem>
                <MenuItem value={20}>20 per page</MenuItem>
                <MenuItem value={50}>50 per page</MenuItem>
                <MenuItem value={100}>100 per page</MenuItem>
              </Select>
            </FormControl>

            <Pagination
              count={totalPages || 1}
              page={currentPage}
              onChange={(_, page) => onPageChange(null, page - 1)}
              shape="rounded"
              size="small"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontFamily: PROFILE_FONT,
                  fontSize: 13,
                  color: DARK_NAVY_LIGHT,
                  borderRadius: "8px",
                },
                "& .Mui-selected": {
                  bgcolor: `${ICON_BLUE} !important`,
                  color: "#ffffff !important",
                },
              }}
            />
          </Box>
        </Box>
      </ListingPageShell>

      <RegisterForm
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
      />
    </>
  );
};

export default PartnerDetailsTable;
